import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import DashboardLayout from '../../components/DashboardLayout';
import { apiRequest } from '../../utils/api';
import { getActiveCompanyId } from '../../utils/companyContext';

const STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
];

const DEFAULT_FORM_STATE = {
  companyId: '',
  shiftName: '',
  inTime: '',
  outTime: '',
  displayOrder: '',
  status: 'ACTIVE',
};

const normalizeTime = (value) => {
  if (!value) return '';
  if (value.length >= 5) {
    return value.slice(0, 5);
  }
  return value;
};

const parseNumber = (value) => {
  if (value === '' || value === null || value === undefined) {
    return null;
  }
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const ShiftSettings = () => {
  const navigate = useNavigate();
  const [activeCompanyId, setActiveCompanyId] = useState(getActiveCompanyId());
  const activeCompanyValue = activeCompanyId ? String(activeCompanyId) : '';
  const [formData, setFormData] = useState({ ...DEFAULT_FORM_STATE, companyId: activeCompanyValue });
  const [formErrors, setFormErrors] = useState({});
  const [shifts, setShifts] = useState([]);
  const [companies, setCompanies] = useState([]);

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [actionId, setActionId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const resetForm = () => {
    setFormData({ ...DEFAULT_FORM_STATE, companyId: activeCompanyValue });
    setFormErrors({});
    setEditingId(null);
  };

  const fetchCompanies = useCallback(async () => {
    try {
      const response = await apiRequest('/api/admin/companies', {}, navigate);
      setCompanies(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setError(err.message || 'Unable to load companies.');
    }
  }, [navigate]);

  const fetchShifts = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      if (!activeCompanyValue) {
        setShifts([]);
        setError('Please select a company from the top header.');
        return;
      }

      const query = new URLSearchParams({ companyId: activeCompanyValue });
      const endpoint = `/api/admin/settings/shifts?${query.toString()}`;
      const response = await apiRequest(endpoint, {}, navigate);
      setShifts(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setError(err.message || 'Unable to load shifts.');
    } finally {
      setLoading(false);
    }
  }, [activeCompanyValue, navigate]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  useEffect(() => {
    fetchShifts();
  }, [fetchShifts]);

  useEffect(() => {
    const syncActiveCompany = () => setActiveCompanyId(getActiveCompanyId());
    window.addEventListener('activeCompanyChanged', syncActiveCompany);
    window.addEventListener('storage', syncActiveCompany);
    return () => {
      window.removeEventListener('activeCompanyChanged', syncActiveCompany);
      window.removeEventListener('storage', syncActiveCompany);
    };
  }, []);

  useEffect(() => {
    setFormData((prev) => ({ ...prev, companyId: activeCompanyValue }));
  }, [activeCompanyValue]);

  const validateForm = () => {
    const errors = {};
    const trimmedName = formData.shiftName.trim();

    if (!formData.companyId) {
      errors.companyId = 'Company is required.';
    }

    if (!trimmedName) {
      errors.shiftName = 'Shift name is required.';
    }

    if (!formData.inTime) {
      errors.inTime = 'In time is required.';
    }

    if (!formData.outTime) {
      errors.outTime = 'Out time is required.';
    }

    const displayOrderNumber = parseNumber(formData.displayOrder);
    if (displayOrderNumber === null || displayOrderNumber < 0) {
      errors.displayOrder = 'Display order must be a non-negative number.';
    }

    if (!STATUS_OPTIONS.some((option) => option.value === formData.status)) {
      errors.status = 'Select a valid status.';
    }

    return { errors, trimmedName, displayOrderNumber };
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const { errors, trimmedName, displayOrderNumber } = validateForm();

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      setError('');
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccessMessage('');

    const payload = {
      companyId: Number(activeCompanyValue),
      shiftName: trimmedName,
      inTime: formData.inTime,
      outTime: formData.outTime,
      displayOrder: displayOrderNumber,
      status: formData.status,
    };

    try {
      const isEditing = Boolean(editingId);
      const endpoint = isEditing
        ? `/api/admin/settings/shifts/${editingId}`
        : '/api/admin/settings/shifts';
      const method = isEditing ? 'PUT' : 'POST';

      const response = await apiRequest(
        endpoint,
        {
          method,
          body: payload,
        },
        navigate
      );

      setSuccessMessage(
        response.message || (isEditing ? 'Shift updated successfully.' : 'Shift created successfully.')
      );
      resetForm();
      fetchShifts();
    } catch (err) {
      setError(err.message || 'Unable to save shift.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (shift) => {
    setEditingId(shift.id);
    setFormData({
      companyId: activeCompanyValue,
      shiftName: shift.shiftName || '',
      inTime: normalizeTime(shift.inTime),
      outTime: normalizeTime(shift.outTime),
      displayOrder: shift.displayOrder !== null && shift.displayOrder !== undefined
        ? String(shift.displayOrder)
        : '',
      status: shift.status || 'ACTIVE',
    });
    setFormErrors({});
    setError('');
    setSuccessMessage('');
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Delete shift?',
      text: 'This will disable the shift without removing it permanently.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, delete',
    });

    if (!result.isConfirmed) {
      return;
    }

    setActionId(id);
    setError('');
    setSuccessMessage('');

    try {
      const response = await apiRequest(
        `/api/admin/settings/shifts/${id}`,
        { method: 'DELETE' },
        navigate
      );
      setSuccessMessage(response.message || 'Shift deleted successfully.');
      if (editingId === id) {
        resetForm();
      }
      fetchShifts();
    } catch (err) {
      setError(err.message || 'Unable to delete shift.');
    } finally {
      setActionId(null);
    }
  };

  const handleStatusChange = async (shift) => {
    const nextStatus = shift.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
    const result = await Swal.fire({
      title: nextStatus === 'ACTIVE' ? 'Activate shift?' : 'Deactivate shift?',
      text: `Are you sure you want to set this shift as ${nextStatus.toLowerCase()}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#198754',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, continue',
    });

    if (!result.isConfirmed) {
      return;
    }

    setActionId(shift.id);
    setError('');
    setSuccessMessage('');

    try {
      const response = await apiRequest(
        `/api/admin/settings/shifts/${shift.id}/status`,
        {
          method: 'PATCH',
          body: { status: nextStatus },
        },
        navigate
      );
      setSuccessMessage(response.message || 'Shift status updated successfully.');
      if (editingId === shift.id) {
        setFormData((prev) => ({ ...prev, status: nextStatus }));
      }
      fetchShifts();
    } catch (err) {
      setError(err.message || 'Unable to update shift status.');
    } finally {
      setActionId(null);
    }
  };

  const companyOptions = useMemo(() => {
    if (!Array.isArray(companies)) {
      return [];
    }

    return companies
      .filter((company) => (company.status ? company.status === 'active' : true))
      .map((company) => ({
        value: String(company.id),
        label: company.name || `Company #${company.id}`,
      }));
  }, [companies]);

  const getCompanyName = (companyId) => {
    const company = companyOptions.find((option) => option.value === String(companyId));
    return company?.label || 'Unassigned';
  };

  const isEditing = Boolean(editingId);

  const sortedShifts = useMemo(() => {
    if (!Array.isArray(shifts)) {
      return [];
    }
    return [...shifts].sort((a, b) => {
      const orderA = a.displayOrder ?? Number.MAX_SAFE_INTEGER;
      const orderB = b.displayOrder ?? Number.MAX_SAFE_INTEGER;
      if (orderA !== orderB) {
        return orderA - orderB;
      }
      return (a.shiftName || '').localeCompare(b.shiftName || '');
    });
  }, [shifts]);

  return (
    <DashboardLayout>
      <div className="container py-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h4>Shift Settings</h4>
          <button className="btn btn-outline-secondary" onClick={() => navigate('/settings')}>
            Back
          </button>
        </div>

        <div className="row">
          <div className="col-md-5 col-lg-4 mb-4">
            <div className="card">
              <div className="card-body">
                <h5 className="card-title mb-4">{isEditing ? 'Update Shift' : 'Add Shift'}</h5>

                {error && (
                  <div className="alert alert-danger" role="alert">
                    {error}
                  </div>
                )}

                {successMessage && (
                  <div className="alert alert-success" role="alert">
                    {successMessage}
                  </div>
                )}

                <form onSubmit={handleSubmit} noValidate>
                  <div className="mb-3">
                    <label htmlFor="shiftName" className="form-label">
                      Shift Name *
                    </label>
                    <input
                      id="shiftName"
                      type="text"
                      className={`form-control ${formErrors.shiftName ? 'is-invalid' : ''}`}
                      value={formData.shiftName}
                      onChange={(event) => {
                        setFormData((prev) => ({ ...prev, shiftName: event.target.value }));
                        if (formErrors.shiftName) {
                          setFormErrors((prev) => {
                            const { shiftName, ...rest } = prev;
                            return rest;
                          });
                        }
                      }}
                      placeholder="Enter shift name"
                    />
                    {formErrors.shiftName && (
                      <div className="invalid-feedback">{formErrors.shiftName}</div>
                    )}
                  </div>

                  <div className="mb-3">
                    <label htmlFor="companyId" className="form-label">
                      Company *
                    </label>
                    <select
                      id="companyId"
                      className={`form-select ${formErrors.companyId ? 'is-invalid' : ''}`}
                      value={formData.companyId}
                      onChange={() => {}}
                      disabled
                    >
                      {activeCompanyValue ? (
                        <option value={activeCompanyValue}>{getCompanyName(activeCompanyValue)}</option>
                      ) : (
                        <option value="">Select company</option>
                      )}
                      {false && companyOptions.map((company) => (
                        <option key={company.value} value={company.value}>
                          {company.label}
                        </option>
                      ))}
                    </select>
                    {formErrors.companyId && (
                      <div className="invalid-feedback">{formErrors.companyId}</div>
                    )}
                  </div>

                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label htmlFor="inTime" className="form-label">
                        In Time *
                      </label>
                      <input
                        id="inTime"
                        type="time"
                        className={`form-control ${formErrors.inTime ? 'is-invalid' : ''}`}
                        value={formData.inTime}
                        onChange={(event) => {
                          setFormData((prev) => ({ ...prev, inTime: event.target.value }));
                          if (formErrors.inTime) {
                            setFormErrors((prev) => {
                              const { inTime, ...rest } = prev;
                              return rest;
                            });
                          }
                        }}
                      />
                      {formErrors.inTime && (
                        <div className="invalid-feedback">{formErrors.inTime}</div>
                      )}
                    </div>
                    <div className="col-md-6 mb-3">
                      <label htmlFor="outTime" className="form-label">
                        Out Time *
                      </label>
                      <input
                        id="outTime"
                        type="time"
                        className={`form-control ${formErrors.outTime ? 'is-invalid' : ''}`}
                        value={formData.outTime}
                        onChange={(event) => {
                          setFormData((prev) => ({ ...prev, outTime: event.target.value }));
                          if (formErrors.outTime) {
                            setFormErrors((prev) => {
                              const { outTime, ...rest } = prev;
                              return rest;
                            });
                          }
                        }}
                      />
                      {formErrors.outTime && (
                        <div className="invalid-feedback">{formErrors.outTime}</div>
                      )}
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label htmlFor="displayOrder" className="form-label">
                        Display Order *
                      </label>
                      <input
                        id="displayOrder"
                        type="number"
                        min="0"
                        className={`form-control ${formErrors.displayOrder ? 'is-invalid' : ''}`}
                        value={formData.displayOrder}
                        onChange={(event) => {
                          setFormData((prev) => ({ ...prev, displayOrder: event.target.value }));
                          if (formErrors.displayOrder) {
                            setFormErrors((prev) => {
                              const { displayOrder, ...rest } = prev;
                              return rest;
                            });
                          }
                        }}
                        placeholder="0"
                      />
                      {formErrors.displayOrder && (
                        <div className="invalid-feedback">{formErrors.displayOrder}</div>
                      )}
                    </div>
                    <div className="col-md-6 mb-3">
                      <label htmlFor="status" className="form-label">
                        Status *
                      </label>
                      <select
                        id="status"
                        className={`form-select ${formErrors.status ? 'is-invalid' : ''}`}
                        value={formData.status}
                        onChange={(event) => {
                          setFormData((prev) => ({ ...prev, status: event.target.value }));
                          if (formErrors.status) {
                            setFormErrors((prev) => {
                              const { status, ...rest } = prev;
                              return rest;
                            });
                          }
                        }}
                      >
                        {STATUS_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      {formErrors.status && (
                        <div className="invalid-feedback">{formErrors.status}</div>
                      )}
                    </div>
                  </div>

                  <div className="d-flex gap-2">
                    <button type="submit" className="btn btn-danger" disabled={submitting}>
                      {submitting ? 'Saving...' : isEditing ? 'Update Shift' : 'Add Shift'}
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={resetForm}
                      disabled={submitting}
                    >
                      Reset
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>

          <div className="col-md-7 col-lg-8">
            <div className="card h-100">
              <div className="card-body">
                <div className="d-flex flex-column flex-lg-row justify-content-between gap-3 mb-3">
                  <div className="d-flex align-items-center gap-2">
                    <h5 className="card-title mb-0">Existing Shifts</h5>
                    {loading && (
                      <div className="spinner-border text-danger" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    )}
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <label htmlFor="companyFilter" className="form-label mb-0 text-nowrap">
                      Company
                    </label>
                    <select
                      id="companyFilter"
                      className="form-select form-select-sm"
                      value={activeCompanyValue}
                      onChange={() => {}}
                      disabled
                    >
                      {activeCompanyValue ? (
                        <option value={activeCompanyValue}>{getCompanyName(activeCompanyValue)}</option>
                      ) : (
                        <option value="">Select company</option>
                      )}
                      {false && companyOptions.map((company) => (
                        <option key={company.value} value={company.value}>
                          {company.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {sortedShifts.length === 0 && !loading ? (
                  <div className="alert alert-info mb-0" role="alert">
                    No shifts found. Add a shift to get started.
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover align-middle">
                      <thead>
                        <tr>
                          <th scope="col">#</th>
                          <th scope="col">Shift Name</th>
                          <th scope="col">Company Name</th>
                          <th scope="col">In Time</th>
                          <th scope="col">Out Time</th>
                          <th scope="col">Display Order</th>
                          <th scope="col">Status</th>
                          <th scope="col" className="text-end">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedShifts.map((shift, index) => {
                          const isProcessing = actionId === shift.id;
                          return (
                            <tr key={shift.id}>
                              <th scope="row">{index + 1}</th>
                              <td>{shift.shiftName}</td>
                              <td>{shift.companyName || getCompanyName(shift.companyId)}</td>
                              <td>{normalizeTime(shift.inTime)}</td>
                              <td>{normalizeTime(shift.outTime)}</td>
                              <td>{shift.displayOrder ?? '-'}</td>
                              <td>
                                <span
                                  className={`badge ${
                                    shift.status === 'ACTIVE' ? 'bg-success' : 'bg-secondary'
                                  }`}
                                >
                                  {shift.status === 'ACTIVE' ? 'Active' : 'Inactive'}
                                </span>
                              </td>
                              <td className="text-end">
                                <div className="btn-group" role="group" aria-label="Shift actions">
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={() => handleEdit(shift)}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => handleStatusChange(shift)}
                                    disabled={isProcessing}
                                  >
                                    {isProcessing
                                      ? 'Updating...'
                                      : shift.status === 'ACTIVE'
                                      ? 'Deactivate'
                                      : 'Activate'}
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() => handleDelete(shift.id)}
                                    disabled={isProcessing}
                                  >
                                    {isProcessing ? 'Processing...' : 'Delete'}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ShiftSettings;
